import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { DeleteModal, ProfileDetails } from "../v3";
import { CRMService } from "../../services";
import { DELETE_CLIENT, EDIT_CLIENT } from "../../constants";
import { AddEditClient } from "./AddEditClient.component";
import { getBalanceColor, useHasPermission } from "../../utils";
import { CLIENT_AVATAR } from "../../utils/image.constants";

export const ClientDetails = ({ onSuccess = () => {}, reloadData }) => {
  const [clientDetails, setClientDetails] = useState({});
  const [isActive, setIsActive] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    clientId: null,
  });

  const { id } = useParams();
  const navigate = useNavigate();

  const hasClientEdit = useHasPermission(EDIT_CLIENT);
  const hadClientDelete = useHasPermission(DELETE_CLIENT);

  useEffect(() => {
    fetchData(id);
  }, [id, reloadData]);

  const fetchData = async (clientId) => {
    try {
      const { data } = await CRMService.getClientById(clientId);
      setClientDetails(data);
      setIsActive(data?.active);
      onSuccess(data?.name);
    } catch (error) {
      toast.error("Failed to fetch client details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = () => {
    setDeleteModal({
      isOpen: true,
      clientId: id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      clientId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await CRMService.deleteClient(deleteModal.clientId);
      toast.success("Client deleted successfully");
      closeDeleteModal();
      navigate(-1);
    } catch (error) {
      console.error("🚀 ~ handleDelete ~ error:", error);
      toast("Failed to delete client", {
        type: "error",
      });
    }
  };

  const handleStatusChange = async (e) => {
    const updatedStatus = e.target.checked;
    try {
      await CRMService.updateClient(id, { active: updatedStatus });
      setIsActive(updatedStatus);
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update status");
      console.error("🚀 ~ handleStatusChange ~ error:", error);
    }
  };

  const invertBalance = (balance) => -(Number(balance) || 0);

  return (
    <>
      <ProfileDetails
        profileImage={CLIENT_AVATAR}
        name={clientDetails?.name}
        phone={clientDetails?.primaryPhone}
        isActive={isActive}
        canToggle={hasClientEdit}
        canEdit={hasClientEdit}
        canDelete={hadClientDelete}
        onEdit={handleOpenModal}
        onDelete={openDeleteModal}
        onToggle={(e) => handleStatusChange(e)}
        infoFields={[
          {
            label: "Balance",
            value: (() => {
              const balance = Number(clientDetails?.account?.balance) || 0;
              if (balance > 0) return `₹ ${balance}`;
              if (balance < 0) return `- ₹ ${Math.abs(balance)}`;
              return `₹ 0.00`;
            })(),
            color: getBalanceColor(invertBalance(clientDetails?.account?.balance)),
          },
          { label: "GST Number", value: clientDetails?.taxId || "-" },
          { label: "Place", value: clientDetails?.primaryAddress?.locality || "-" },
        ]}
      />

      {/* edit client modal */}
      <AddEditClient open={openModal} clientId={id} onClose={handleCloseModal} onSuccess={() => fetchData(id)} />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this client?"
      />
    </>
  );
};
